import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { Guards } from 'interfaces/guards'
import { createSelector } from 'reselect'
import { isOtherPeergroupRankSelectedSelector, networthSummaryRankSelector } from 'selectors/networthSelectors'
import NetworthRank, { NetworthRankProps, NetworthRankStateProps } from 'components/features/pfm/NetworthRank/NetworthRank'
import { networthSummaryRankGuard } from 'guards/networthGuards'
import { PeergroupRelation } from 'interfaces/peergroups'
import { peersRelationIdSelector, selectedRelationIdSelector, selectedRelationSelector } from 'selectors/peergroupSelectors'
import { connectOptionallyAdvanced, createPositiveRendererProps, NO_RENDER, OptionalRendererProps } from 'tangle-shared'
import { State } from 'interfaces/state'
import { setNetworthSummaryRelationActionCreator } from 'actions/networthActions'
import { StoreDispatch } from 'interfaces/component'

const SELECTED_RELATION_TITLES: Record<string, string> = {
  everyone: 'National Ranking',
  'similar-location': 'Rank in Your Area',
  'similar-income': 'Rank in Your Income Range',
  'similar-age': 'Rank in Your Age Range',
  random: 'Rank in Random Group',
}

const getSelectedRelationTitle = ({ slug, name }: PeergroupRelation): string =>
  slug && SELECTED_RELATION_TITLES[slug]
    ? SELECTED_RELATION_TITLES[slug]
    : `Rank in "${ name }" Group`

type NetworthRankContainerOptionalProps = {
  title: string | null
  value: number | null
  isOtherPeergroupSelected: boolean | null
}

const createMapStateToProps = () => createSelector(
  networthSummaryRankSelector,
  selectedRelationSelector,
  isOtherPeergroupRankSelectedSelector,
  (
    value,
    selectedRelation,
    isOtherPeergroupSelected,
  ): NetworthRankContainerOptionalProps => {
    const title = !selectedRelation
      ? null
      : isOtherPeergroupSelected
        ? getSelectedRelationTitle(selectedRelation)
        : 'Your Peer Ranking'

    return {
      title,
      value,
      isOtherPeergroupSelected,
    }
  },
)

const selectorFactory = (dispatch: StoreDispatch) =>
  (nextState: State, nextOwnProps: NetworthRankContainerOptionalProps): OptionalRendererProps<NetworthRankProps> => {
    const {
      title,
      value,
      isOtherPeergroupSelected,
    } = nextOwnProps

    if (title === null || value === null || isOtherPeergroupSelected === null) {
      return NO_RENDER()
    }

    const selectedRelation = selectedRelationSelector(nextState)

    if (!selectedRelation) {
      return NO_RENDER()
    }

    const nextRelationId = isOtherPeergroupSelected
      ? peersRelationIdSelector(nextState)
      : selectedRelationIdSelector(nextState)

    if (!nextRelationId) {
      return NO_RENDER()
    }

    const props = createPositiveRendererProps<NetworthRankProps>({
      title,
      value,
      isOtherPeergroupSelected,
      onClick: () => dispatch(setNetworthSummaryRelationActionCreator(nextRelationId)),
    })

    return props
  }

const guards: Guards = [
  networthSummaryRankGuard,
]

const NetworthRankContainerOptional = connectOptionallyAdvanced<
  NetworthRankStateProps, {}, State
  >(selectorFactory)(NetworthRank)

const NetworthRankContainer = connectShadedLoadable<
  // TODO
  {}, {}, {}, {}
  >(createMapStateToProps, undefined, guards)(NetworthRankContainerOptional)

export default NetworthRankContainer
